
import React from 'react';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LayoutProvider } from "@/contexts/LayoutContext";
import { OverdueTasksProvider } from "@/components/NotificationBanner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { WorkHistoryProvider } from "@/contexts/WorkHistoryContext";
import { useLogRocket } from "@/hooks/use-log-rocket";

// Initialize LogRocket hook wrapper
const LogRocketInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useLogRocket();
  return <>{children}</>;
};

// Create a client with optimized configuration for auth
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry if the error is from authentication
        if (
          typeof error === 'object' && 
          error !== null && 
          'message' in error && 
          typeof error.message === 'string' && 
          (
            error.message.includes('auth') || 
            error.message.includes('token') ||
            error.message.includes('session')
          )
        ) {
          return false;
        }
        
        // Don't retry on foreign key constraint errors
        if (
          typeof error === 'object' && 
          error !== null && 
          'code' in error && 
          (error.code === '23503' || // Foreign key violation
           error.message?.includes('foreign key constraint') ||
           error.message?.includes('violates foreign key'))
        ) {
          console.error('Foreign key constraint violation, not retrying:', error);
          return false;
        }
        
        // Only retry a few times for other errors
        return failureCount < 2;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: true,
      refetchOnReconnect: true, // Refetch data when reconnecting
      refetchInterval: false, // Don't refetch periodically by default
    },
  },
});

interface AppProvidersProps {
  children: React.ReactNode;
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <React.StrictMode>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <LogRocketInitializer>
              <LayoutProvider>
                <OverdueTasksProvider>
                  <WorkHistoryProvider>
                    <TooltipProvider>
                      {children}
                      <Toaster />
                    </TooltipProvider>
                  </WorkHistoryProvider>
                </OverdueTasksProvider>
              </LayoutProvider>
            </LogRocketInitializer>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
}
